/* eslint-disable @typescript-eslint/no-explicit-any */
import { IPG, WordItem } from "@/types/summary";
import React, { useEffect, useRef, useState } from "react";
import MarkIcon from "@/assets/common/ic_mark.svg?react";
import CopyIcon from "@/assets/common/ic_copy.svg?react";
import { formatRecordTime } from "@/utils/formatter";
import SpeakerPopover from "./SpeakerPopover";
import EditRecordItem, { EditRecordItemRef } from "@/component/EditRecordItem";
import { message } from "antd";
import summaryCommonStore from "@/store/summaryCommon.store";
import summarySrcStore from "@/store/summarySrc.store";
import Popover from "@/component/PopoverRc";
import RecordHelper from "@/bussiness/RecordHelper";
import { saveDoc } from "@/services/api_record";
import recordStore from "@/store/record.store";
import SpeakerAvatar from "@/pages/Voice/SpearkerAvatar";
import { getSpeakerAvatarInfo } from "@/utils/newAvatar";
import usePermission from "@/sections/Summary/usePermission";
import { formatDocTc } from "@/utils/formatDocTc";
import Tooltip from "@/component/Tooltip";
import fridayReporter from "@/utils/friday";
import { SRC_CONTENT_CLASSNAME } from "@/constants/record";
import "./index.css";
import avPlayerStore from "@/store/avPlayer.store";
import AVPlayerHelper from "@/bussiness/AVPlayerHelper";

export interface EditItem {
  wi: string;
  newContent: string;
}

export interface IDocItemProps {
  record: IPG;
  avatarSrc?: string;
  /** 占位 */
  placeholder?: string;
  /** 是否为编辑态 */
  isEditing?: boolean;
  editable?: boolean;
  isPlaying?: boolean;
  underlineOnFocus?: boolean;
  recomputeRowHeights?: () => void;
  onSplit?: (pIndex?: number) => void;
  onMerge?: (pIndex?: number) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
const DocItem = (props: IDocItemProps) => {
  const { record, avatarSrc, editable, onSplit, onMerge, recomputeRowHeights, ...rest } = props;
  const [hasMark, setHasMark] = useState(false);
  const [speakerPopoverVisible, setSpeakerPopoverVisible] = useState(false);

  const editItemRef = useRef<EditRecordItemRef>(null);

  const audioInfo = summaryCommonStore((state) => state.audioInfo);
  const detailInfo = summarySrcStore((state) => state.detailInfo);

  const { setRecordEditing } = summarySrcStore.getState();

  const records = recordStore((state) => state.records);
  const { setTempRecords } = recordStore.getState();
  const { isOwner, allowBackUp, showEdit } = usePermission();
  const recordEditing = summarySrcStore((state) => state.recordEditing);

  const handleDoubleClick = () => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Text_Edit",
    });
    if (!showEdit) {
      return;
    }
    if (!isOwner && allowBackUp) {
      message.warning("创建副本后可使用该功能");
      return;
    }
    setTempRecords(records);
    setRecordEditing(true);
  };

  const handleClickWordItem = (bt: number) => {
    audioJumpTo(bt);
    AVPlayerHelper.textJumpCb();
  };

  const handleSave = async () => {
    try {   
      const formatRecords = formatDocTc([...recordStore.getState().records]);
      await saveDoc({
        streamName: detailInfo?.streamName || "",
        srcDoc: {
          pg: formatRecords,
        },
      });
      setSpeakerPopoverVisible(false);
    } catch (error) {
      console.log('handleSave error', error);
    }
  };

  const handleEditSingle = async (name: string) => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Text_ReplaceOneSpeaker",
    });
    RecordHelper.modifySpeakerName(record.pi, [name]);
    handleSave();
  };

  const handleEditAll = async (name: string, oldSpeakerName = "") => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Text_ReplaceAllSpeaker",
    });
    RecordHelper.modifyAllSameSpeakerName([oldSpeakerName], [name]);
    handleSave();
  };

  const handleEdit = (
    pi: string,
    editItems: EditItem[],
    emptyWordAllowed = false
  ) => {
    RecordHelper.editRecord(pi, editItems, {
      isLastRecord: false,
      emptyWordAllowed,
    });
  };

  const handleSplit = (editItem: EditItem, cIndex: number, pi: string) => {
    const pIndex = RecordHelper.splitRecord(pi, editItem.wi, cIndex);
    onSplit?.(pIndex);
  };

  const handleMerge = (pi: string) => {
    const pIndex =RecordHelper.mergeRecord(pi);
    onMerge?.(pIndex);
  };

  const handleMark = async (pi: string, signFlag: boolean) => {
    fridayReporter.pushData({
      eventCode: signFlag ? "SummaryPage_Text_Mark" : "SummaryPage_Text_Unmark",
    });
    RecordHelper.signRecord(pi, signFlag);
    handleSave();
  };

  const handleCopy = () => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Text_Copy",
    });
    const text = editItemRef.current?.getText();
    if (text) {
      navigator.clipboard.writeText(text);
      message.success("复制成功");
    }
  };

  const audioJumpTo = (time: number) => {
    const _time = [0, time / 1000, detailInfo!.duration || Infinity].sort(
      (a, b) => a - b
    )[1];
    const audioPlayerIns = avPlayerStore.getState().audioPlayerIns;
    audioPlayerIns?.setCurrentTime(_time);
  };

  useEffect(() => {
    const words = record.sc.reduce(
      (acc, cur) => acc.concat(cur.words),
      [] as WordItem[]
    );
    const hasMark = words.some((word) => word.mark);
    setHasMark(hasMark);
  }, [record]);

  const speakerAvatarInfo = getSpeakerAvatarInfo(record.headImage);
  const formatSpeakerName = record.speakerName?.[0] || "发言人";
  return (
    <div className="item-box mb-5 flex" id="doc-item" data-p-id={record.pi}>
      {record.headImage ? (
        <SpeakerAvatar
          speakerName={record.speakerName[0]}
          avatarIndex={speakerAvatarInfo.avatarIndex}
          useDefaultAvatarIcon={record.useDefaultIcon}
          className="h-6 size-6 mr-2"
          fontSize="0.56rem"
        />
      ) : (
        <img src={avatarSrc} className="h-6 size-4 mr-2" />
      )}
      <div className="flex-1">
        {/* 发言人 */}
        <div className="flex text-#A1A7B2 text-3 font-400 relative">
          <div className="h-6 flex items-center">
            <Popover
              destroyTooltipOnHide
              hover={false}
              placement="bottomLeft"
              onVisibleChange={(vis) => {
                if(!editable) return;
                setSpeakerPopoverVisible(vis);
                if (vis) {
                  fridayReporter.pushData({
                    eventCode: "SummaryPage_Text_EditSpeaker",
                  });
                }
              }}
              className="pt-0"
              visible={speakerPopoverVisible}
              content={
                <SpeakerPopover
                  originValue={formatSpeakerName}
                  onEditSingle={handleEditSingle}
                  onEditAll={handleEditAll}
                />
              }
            >
              <div className={`relative left--1 mr-1 ${editable && 'hover:bg-#ECEEF3 cursor-pointer'} py-0.875 px-1 rounded-1`}>
                {formatSpeakerName}
              </div>
            </Popover>
            <span className="py-0.875">
              {formatRecordTime(
                (record.sc[0]?.bt || 0) / 1000
              ) || "00:00"}
            </span>
          </div>
          <div className="item-tools right-0 bottom-0 top-0 m-auto absolute flex items-center">
            <div className="size-6 flex items-center justify-center mr-2">
              {editable && <Tooltip content={hasMark ? "取消标记" : "标记"} hover={true}>
                <MarkIcon
                  className="size-4 cursor-pointer"
                  onClick={() => handleMark?.(record.pi, !hasMark)}
                />
              </Tooltip>}
            </div>
            <div className="size-6 flex items-center justify-center">
              <Tooltip content="复制" hover={true}>
                <CopyIcon
                  className="size-4 cursor-pointer opacity-60"
                  onClick={handleCopy}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        {/* 编辑框 */}
        <div className={`${SRC_CONTENT_CLASSNAME} mt-1 pr-1.5`}>
          <EditRecordItem
            className={`font-400 !color-#30343C ${!recordEditing && audioInfo?.audioSrc && 'cursor-pointer'}`}
            record={record}
            ref={editItemRef}
            handleDoubleClick={handleDoubleClick}
            handleClickWordItem={handleClickWordItem}
            handleMerge={handleMerge}
            handleEnter={handleSplit}
            handleEdit={handleEdit}
            recomputeRowHeights={recomputeRowHeights}
            {...rest}
          />
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export default React.memo(DocItem);
